import { action, computed, observable } from "mobx";
import MessageCenter from 'cd-eft/components/common/MessageCenter/MessageCenterVM';
import stores from "cd-eft/models/Stores";
import { backFillSoilTypeConfig, UGOInputsConfig } from "./UGOInputs.labels.config";
import { DesignElementController, UgoDesignToolsController } from 'cd-eft/services/eftSetApi';
import { UgoAlignedElementDesignParamsRequestDTO, UgoAlignedElementDesignParamsRequestDTOBackFillSoilTypeEnum } from "cd-eft/_codegen_/openapi-generator/eft-set";
import { appConfig } from 'cd-eft/config/AppConfig';
import { EngineeringMapService } from 'cd-eft/framework/mapServices/EngineeringMapServices';

const { designStore, ugoDesignStore } = stores.AppStore;
export class UGOInputsVM {
  history: any;
  constructor(history) {
    this.history = history;
  }

  @observable ugoInputsConfig = UGOInputsConfig;
  @observable loading: boolean = false;
  @observable canSetPressureFlow = false;

  @observable.deep formsValidation = {
    UGOInfoSection: {
      ugoName: false,
      backFillSoilType: false,
      pipelineBenchmark: true,
      constructionBenchmark: true,
      benchmarkElevation: true,
      benchmarkDescription: true,
    },
    AlignmentSection: {
      alignment: false,
    },
    PipelineDefaultSection: {
      material: false,
      pressurizable: true,
      pressureFlow: true,
      pipeSize: false,
    },
  };

  @observable.deep redHighlighting = {
    UGOInfoSection: {
      ugoName: false,
      backFillSoilType: false,
      pipelineBenchmark: false,
      constructionBenchmark: false,
      benchmarkElevation: false,
      benchmarkDescription: false,
    },
    AlignmentSection: {
      alignment: false,
    },
    PipelineDefaultSection: {
      material: false,
      pressurizable: false,
      pressureFlow: false,
      pipeSize: false,
    },
  };

  @observable elementActions = {
    Edit: { action: () => { } },
    Delete: { action: () => { } },
    Add: { action: () => { } },
  };

  @observable onClickInformation = false;

  @observable hasSectionChanged = {
    UGOInfoSection: false,
    AlignmentSection: false,
    PipelineDefaultSection: false,
  };

  isAllTrue(obj) {
    if (typeof obj !== 'object' || obj === null) return false;

    for (const value of Object.values(obj)) {
      if (value && typeof value === 'object') {
        if (!this.isAllTrue(value)) return false;
      } else if (value !== true) {
        return false;
      }
    }
    return true;
  }

  //For future PR's when field validations & uiState stories are addressed
  @computed get disableSaveButton() {
    return (
      !(Object.values(this.hasSectionChanged).some(e => e === true) && this.minimumRequirementForSave)
    )
  }

  @computed get disableSaveAndProceedButton() {
    return (
      !(Object.values(this.hasSectionChanged).some(e => e === true) && this.minimumRequirementForSave)

    )
  }

  @computed get minimumRequirementForSave() {
    return this.isAllTrue(this.formsValidation.UGOInfoSection) && this.isAllTrue(this.formsValidation.AlignmentSection) && this.isAllTrue(this.formsValidation.PipelineDefaultSection);
  }

  @action.bound onClickInformationToggle = () => {
    this.onClickInformation = !this.onClickInformation;
  };

  @action.bound async getAlignmentList() {
    try {
      const response = await DesignElementController.getAllDesignElementsBySessionAndTypeUsingGET(
        designStore.projectsStore.currentSessionId,
        'Alignment'
      );
      const newOptions = response?.data?.map((element) => ({
        name: element.designElementName,
        value: String(element.designElementId),
        optionDisabled: element.flagUpToDate!,
      }));
      this.ugoInputsConfig.AlignmentSection.alignment.options = newOptions;
    } catch (error) {
      MessageCenter.addError((error as Error).message);
    }
  }

  @action.bound setPressurizable = (value) => {
    const selected = ugoDesignStore.pipelineMaterials.get(parseInt(value));
    const canPressurize = selected?.canPressurizable || false;
    this.canSetPressureFlow = canPressurize;
    if (!canPressurize) {
      ugoDesignStore.inputForms.PipelineDefaultSection.pressurizable = false;
      ugoDesignStore.inputForms.PipelineDefaultSection.pressureFlow = false;
      this.ugoInputsConfig.PipelineDefaultSection.pressurizable.disabled = true;
    } else {
      this.ugoInputsConfig.PipelineDefaultSection.pressurizable.disabled = false;
    }
  };

  @action.bound handleInputsOnChange = (e, section) => {
    const { name, value, type, checked } = e.target;
    ugoDesignStore.ugoInputsChanged = true
    this.hasSectionChanged[section] = true;
    this.redHighlighting[section][name] = false;
    if (section === 'PipelineDefaultSection' && name === 'material') {
      ugoDesignStore.inputForms[section][name] = value ? parseInt(value) : 0;
      this.setPressurizable(value);
    } else {
      ugoDesignStore.inputForms[section][name] = type === 'checkbox' ? checked : value;
    }

  };

  @action.bound handleInputsOnBlur = async (e, section) => {
    const { name, value } = e.target;
    if (section === 'PipelineDefaultSection' && name === 'pipeSize') {
      const numericValue = parseFloat(value);
      if (!isNaN(numericValue)) {
        const isValid = ugoDesignStore.validatePipeSize(numericValue, ugoDesignStore.inputForms.PipelineDefaultSection.material, section, 'pipeSize', 'material', this.formsValidation, this.redHighlighting);
        this.redHighlighting.PipelineDefaultSection[name] = !isValid;
        this.formsValidation.PipelineDefaultSection[name] = isValid;
      }
    } else if (section === 'PipelineDefaultSection' && name === 'material') {
      if (value !== '' && ugoDesignStore.inputForms.PipelineDefaultSection.pipeSize) {
        const isValid = ugoDesignStore.validatePipeSize(ugoDesignStore.inputForms.PipelineDefaultSection.pipeSize, parseInt(value), section, 'pipeSize', 'material', this.formsValidation, this.redHighlighting);
        this.redHighlighting.PipelineDefaultSection[name] = !isValid;
        this.formsValidation.PipelineDefaultSection[name] = isValid;
      }
    } else if (section === 'UGOInfoSection' && name === 'benchmarkElevation') {
      const numericValue = parseFloat(value);
      if (!isNaN(numericValue)) {
        await this.validateBenchmark(numericValue, e, section);
      }
    } else if (section === 'UGOInfoSection' && ['constructionBenchmark', 'benchmarkDescription'].includes(name)) {
      this.validateBenchmarkName();
    } else {
      this.validateField(e, section);
    }
  };

  @action.bound validateField = async (e, section: string) => {
    const { name, value } = e.target;
    const currentField = UGOInputsConfig[section][name];
    if (currentField.required && value === '') {
      if (this.hasSectionChanged[section]) {
        this.formsValidation[section][name] = false;
        this.redHighlighting[section][name] = true;
        await MessageCenter.addNotificationFromCode('Common_17', undefined, currentField.label)
      }
    } else {
      this.formsValidation[section][name] = true;
      this.redHighlighting[section][name] = false;
    }
  }

  @action.bound validateForms = async () => {
    for (const sectionKey of Object.keys(ugoDesignStore.inputForms)) {
      const section = ugoDesignStore.inputForms[sectionKey];
      for (const key of Object.keys(section)) {
        const e = {
          target: {
            name: key,
            value: section[key],
          },
        };
        await this.handleInputsOnBlur(e, sectionKey);
      }
    }
  };

  @action.bound proceedToUGODesign() {
    this.history.push(`/design/${designStore.projectsStore.currentProjectId}/ugo/${ugoDesignStore.ugoDesignObject.alignedElementDesignParamsId}/design`);
  }

  @action.bound getUgoInputs() {
    ugoDesignStore.inputForms.UGOInfoSection.ugoName = ugoDesignStore.ugoDesignObject.alignedFeatureName!;
    ugoDesignStore.inputForms.UGOInfoSection.backFillSoilType = ugoDesignStore.ugoDesignObject.backFillSoilType!;
    ugoDesignStore.inputForms.UGOInfoSection.constructionBenchmark = ugoDesignStore.ugoDesignObject.constructBenchmark!;
    ugoDesignStore.inputForms.UGOInfoSection.benchmarkElevation = ugoDesignStore.ugoDesignObject.benchmarkElevation!;
    ugoDesignStore.inputForms.UGOInfoSection.benchmarkDescription = ugoDesignStore.ugoDesignObject.benchmarkDescription!;
    ugoDesignStore.inputForms.AlignmentSection.alignment = ugoDesignStore.ugoDesignObject.alignmentDesignElementJsonId!;
    const json = ugoDesignStore.ugoDesignObject.ugoPipeSpecification;
    ugoDesignStore.inputForms.PipelineDefaultSection.material = json.materialTypeId ?? 0;
    ugoDesignStore.inputForms.PipelineDefaultSection.pressurizable = json.pressurizable;
    ugoDesignStore.inputForms.PipelineDefaultSection.pressureFlow = json.pressureFlow;
    ugoDesignStore.inputForms.PipelineDefaultSection.pipeSize = json.pipelineSize ?? 0;
    this.setPressurizable(json.materialTypeId);
  }

  @action.bound async initializeDropdownInputsAndRestoreData() {
    this.loading = true;
    await this.getAlignmentList();
    await this.getUgoInputs();
    this.ugoInputsConfig.PipelineDefaultSection.material.options = ugoDesignStore.pipelineMaterialOptions;

    //In case of Edit UGO flow
    if (ugoDesignStore.ugoDesignObject.alignedElementDesignParamsId > 0) {
      // initialize validationForm
      await this.validateForms();
    }
    this.loading = false;
  }

  @action.bound async onClickSave(type: 'save' | 'saveAndProceed') {
    this.validateForms();
    try {
      this.loading = true;
      const selectedMaterial = ugoDesignStore.pipelineMaterials.get(ugoDesignStore.inputForms.PipelineDefaultSection.material);
      const isPerforated = selectedMaterial?.pipelineMaterialName!.includes('Perforated');
      const requestPayload: UgoAlignedElementDesignParamsRequestDTO = {
        alignedElementDesignParamsId: ugoDesignStore.ugoDesignObject.alignedElementDesignParamsId,
        alignedFeatureName: ugoDesignStore.inputForms.UGOInfoSection.ugoName,
        alignmentDesignElementJsonId: +ugoDesignStore.inputForms.AlignmentSection.alignment, //selected AlignmentId
        backFillSoilType: UgoAlignedElementDesignParamsRequestDTOBackFillSoilTypeEnum[backFillSoilTypeConfig[ugoDesignStore.inputForms.UGOInfoSection.backFillSoilType]],
        benchmarkDescription: ugoDesignStore.inputForms.UGOInfoSection.benchmarkDescription,
        benchmarkElevation: ugoDesignStore.inputForms.UGOInfoSection.benchmarkElevation,
        benchmarkName: '',
        comments: '',
        constructBenchmark: ugoDesignStore.inputForms.UGOInfoSection.constructionBenchmark,
        designElementTypeId: appConfig.hardCodes.designElementTypeUGO,
        sessionUuid: designStore.projectsStore.currentSessionId,
        ugoPipeSpecification: {
          materialTypeId: selectedMaterial?.ugoPipelineMaterialTypeId ?? 0,
          ugoMaterialDescription: selectedMaterial?.pipelineMaterialName,
          pipelineSize: ugoDesignStore.inputForms.PipelineDefaultSection.pipeSize,
          perforated: isPerforated,
          pressurizable: ugoDesignStore.inputForms.PipelineDefaultSection.pressurizable,
          pressureFlow: ugoDesignStore.inputForms.PipelineDefaultSection.pressureFlow,
        },
      }
      const response = await UgoDesignToolsController.createOrUpdateUgoAlignedElementDesignParamsUsingPOST(requestPayload);
      const data = response.data;
      const errorResponse = data.errorResponse;
      if (errorResponse && errorResponse.errorType && errorResponse.errorCode && errorResponse.errorMessage) {
        MessageCenter.addNotification(errorResponse.errorMessage, errorResponse.errorType, errorResponse.errorCode);
        return;
      }
      // this.disableSaveButton = true;
      ugoDesignStore.ugoInputsChanged = false;
      if (type === 'save' && requestPayload.alignedElementDesignParamsId === 0 && data.alignedElementDesignParamsId) {
        window.history.replaceState({}, '', `/design/${designStore.projectsStore.currentProjectId}/ugo/${data.alignedElementDesignParamsId}/input`)
      }
      ugoDesignStore.ugoDesignObject.alignedElementDesignParamsId = data.alignedElementDesignParamsId!
      ugoDesignStore.ugoDesignObject.alignedFeatureName = data.alignedFeatureName!

      if (type === 'save') {
        // if (!this.isAllTrue(this.formsValidation)) {
        //   await MessageCenter.addNotificationFromCode('UGO_error_code');
        // }
        await this.loadUGOTerraceShapesToMap();
      } else if (type === 'saveAndProceed') {
        await this.loadUGOTerraceShapesToMap();
        // this.disableSaveAndProceedButton = true;
        this.proceedToUGODesign();
      }

      this.loading = false;
    } catch (error) {
      MessageCenter.addError((error as Error).message);
    } finally {
      this.loading = false;
    }
  }

  @action.bound loadUGOTerraceShapesToMap = async (): Promise<void> => {
    const sessionId = stores.AppStore.designStore.projectsStore.currentSessionId;
    const mapView = EngineeringMapService.instance?.mapView;

    if (!sessionId || !mapView) {
      console.warn('Missing session ID or map view');
      return;
    }

    try {
      const alignmentDesignElementJsonId = Number(ugoDesignStore.inputForms.AlignmentSection.alignment);
      const response = await UgoDesignToolsController.getAllTerracesIntersectingWithUgoAlignmentUsingGET(alignmentDesignElementJsonId);
      const terraceData = response?.data ?? [];

      // Clear existing terrace layers
      stores.AppStore.terraceDesignStore.terraceLayers.forEach(layer => {
        mapView.map.remove(layer);
        layer.destroy();
      });

      stores.AppStore.terraceDesignStore.terraceLayers = [];

      for (const e of terraceData) {
        const terraceId = e.alignedElementDesignParametersId ? e.alignedElementDesignParametersId : -1;
        const terraceTitle = e.alignedFeatureName ? e.alignedFeatureName : `Terrace ${terraceId}`;

        if (terraceId !== -1) {
          if (e.terraceStructureShapeDTO) {
            stores.AppStore.terraceDesignStore.addTerraceToMap(
              e.terraceStructureShapeDTO,
              mapView,
              terraceId,
              terraceTitle
            );
          } else {
            console.error(`No shape data for terrace ID ${terraceId}`);
          }
        }
      }

    } catch (error) {
      console.error('Failed to load terrace shapes:', error);
      MessageCenter.addError((error as Error).message);
    }
  };

  @action.bound async validateBenchmark(value: number, e, section) {
    const currentField = UGOInputsConfig[section][e.target.name];
    if (value < 0) {
      await MessageCenter.addNotificationFromCode('Common_10', undefined, currentField.label);
      this.formsValidation.UGOInfoSection.benchmarkElevation = false;
      this.redHighlighting.UGOInfoSection.benchmarkElevation = true;
      return;
    } else if (value === 0) {
      this.formsValidation.UGOInfoSection.constructionBenchmark = true;
      this.formsValidation.UGOInfoSection.benchmarkDescription = true;
      this.redHighlighting.UGOInfoSection.constructionBenchmark = false;
      this.redHighlighting.UGOInfoSection.benchmarkDescription = false;
    } else {
      this.validateBenchmarkName()
    }
    this.formsValidation.UGOInfoSection.benchmarkElevation = true;
    this.redHighlighting.UGOInfoSection.benchmarkElevation = false;
  }

  @action.bound validateBenchmarkName = async () => {
    if (ugoDesignStore.inputForms.UGOInfoSection.benchmarkElevation > 0) {
      if (ugoDesignStore.inputForms.UGOInfoSection.constructionBenchmark.length === 0) {
        await MessageCenter.addNotificationFromCode('Common_17', undefined, UGOInputsConfig.UGOInfoSection.constructionBenchmark.label);
        this.formsValidation.UGOInfoSection.constructionBenchmark = false;
        this.redHighlighting.UGOInfoSection.constructionBenchmark = true;
      } else if (ugoDesignStore.inputForms.UGOInfoSection.benchmarkDescription.length === 0) {
        await MessageCenter.addNotificationFromCode('Common_17', undefined, UGOInputsConfig.UGOInfoSection.benchmarkDescription.label);
        this.formsValidation.UGOInfoSection.benchmarkDescription = false;
        this.redHighlighting.UGOInfoSection.benchmarkDescription = true;
      } else {
        this.formsValidation.UGOInfoSection.constructionBenchmark = true;
        this.redHighlighting.UGOInfoSection.constructionBenchmark = false;
        this.formsValidation.UGOInfoSection.benchmarkDescription = true;
        this.redHighlighting.UGOInfoSection.benchmarkDescription = false;
      }
    }
  }

  @action.bound togglePressureFlow() {
    if (this.canSetPressureFlow) {
      ugoDesignStore.inputForms.PipelineDefaultSection.pressureFlow =
        !ugoDesignStore.inputForms.PipelineDefaultSection.pressureFlow;
    }
  }
}